import {
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
} from '@tanstack/react-query'
import { apis } from '@/config/apiConfig'
import { createMutationCall } from '@/queries/utils/api-helpers'
import type { Preference, UserInitResponse } from '@/types/user'
import type { MeasurementUnits } from '@/utilities/measurements'
import {
  type MeasurementLabelSettings,
  withMeasurementLabelSettings,
  withMeasurementUnits,
} from '@/utilities/measurementPreferences'
import { toast } from '@/utilities/toast'
import { userKeys } from './use-user'

export interface UpsertPreferenceParams {
  key: string
  name: string
  value: unknown
}

interface MeasurementUnitsPreferenceParams
  extends Omit<UpsertPreferenceParams, 'value'> {
  value: MeasurementUnits
}

interface MeasurementLabelsPreferenceParams
  extends Omit<UpsertPreferenceParams, 'value'> {
  value: MeasurementLabelSettings
}

export const measurementUnitsPreferenceMutationKey = [
  'user-preferences',
  'measurement-units',
] as const

export const measurementLabelsPreferenceMutationKey = [
  'user-preferences',
  'measurement-labels',
] as const

const upsertUserPreference = (
  params: UpsertPreferenceParams
): Promise<unknown> =>
  createMutationCall(
    `${apis.apiDatabase.uri}user/preferences/upsert`,
    'POST',
    params,
    { silent: true }
  )

interface MeasurementPreferenceFieldSequence {
  baselineField: unknown
  latestSuccessfulValue?: unknown
  hasSuccessfulValue: boolean
  restoreUser: (
    current: UserInitResponse | undefined
  ) => UserInitResponse | undefined
  updateUser: (
    current: UserInitResponse | undefined,
    value: unknown
  ) => UserInitResponse | undefined
}

interface MeasurementPreferenceMutationSequence {
  pending: number
  fields: Map<string, MeasurementPreferenceFieldSequence>
}

interface MeasurementPreferenceMutationContext {
  sequence: MeasurementPreferenceMutationSequence
  field: MeasurementPreferenceFieldSequence
}

const measurementPreferenceMutationSequences = new WeakMap<
  QueryClient,
  MeasurementPreferenceMutationSequence
>()

/**
 * Hook for upserting user preferences
 */
export const useUpsertUserPreference = (): UseMutationResult<
  unknown,
  Error,
  UpsertPreferenceParams
> => {
  const queryClient = useQueryClient()

  return useMutation<unknown, Error, UpsertPreferenceParams>({
    mutationFn: upsertUserPreference,
    onSuccess: () => {
      // Invalidate user query to refresh preferences
      void queryClient.invalidateQueries({ queryKey: userKeys.init() })
    },
  })
}

interface OptimisticMeasurementPreferenceOptions<T> {
  mutationKey: readonly string[]
  scopeId: string
  fieldKey: keyof Preference
  updateUser: (
    current: UserInitResponse | undefined,
    value: T
  ) => UserInitResponse | undefined
  errorMessage: string
}

const readPreferenceField = (
  current: UserInitResponse | undefined,
  fieldKey: keyof Preference
): unknown => {
  return current?.data?.preferences?.[fieldKey]
}

const restorePreferenceField = (
  current: UserInitResponse | undefined,
  fieldKey: keyof Preference,
  baselineField: unknown
): UserInitResponse | undefined => {
  if (!current?.data) return current

  const preferences = {
    ...current.data.preferences,
  } as Record<string, unknown>
  if (baselineField === undefined) {
    delete preferences[fieldKey]
  } else {
    preferences[fieldKey] = baselineField
  }

  return {
    ...current,
    data: {
      ...current.data,
      preferences,
    },
  }
}

const useUpsertOptimisticMeasurementPreference = <T>(
  options: OptimisticMeasurementPreferenceOptions<T>
): UseMutationResult<
  unknown,
  Error,
  Omit<UpsertPreferenceParams, 'value'> & { value: T },
  MeasurementPreferenceMutationContext
> => {
  const queryClient = useQueryClient()

  return useMutation<
    unknown,
    Error,
    Omit<UpsertPreferenceParams, 'value'> & { value: T },
    MeasurementPreferenceMutationContext
  >({
    mutationKey: options.mutationKey,
    scope: { id: options.scopeId },
    mutationFn: upsertUserPreference,
    onMutate: async variables => {
      const queryKey = userKeys.init()
      let sequence = measurementPreferenceMutationSequences.get(queryClient)
      if (sequence == null) {
        sequence = {
          pending: 0,
          fields: new Map(),
        }
        measurementPreferenceMutationSequences.set(queryClient, sequence)
      }
      sequence.pending += 1

      await queryClient.cancelQueries({ queryKey })
      let field = sequence.fields.get(options.fieldKey)
      if (field == null) {
        const baselineField = readPreferenceField(
          queryClient.getQueryData<UserInitResponse>(queryKey),
          options.fieldKey
        )
        field = {
          baselineField,
          hasSuccessfulValue: false,
          restoreUser: current =>
            restorePreferenceField(current, options.fieldKey, baselineField),
          updateUser: (current, value) =>
            options.updateUser(current, value as T),
        }
        sequence.fields.set(options.fieldKey, field)
      }
      queryClient.setQueryData<UserInitResponse | undefined>(queryKey, value =>
        options.updateUser(value, variables.value)
      )

      return { sequence, field }
    },
    onSuccess: (_data, variables, context) => {
      if (context != null) {
        context.field.latestSuccessfulValue = variables.value
        context.field.hasSuccessfulValue = true
      }
    },
    onError: () => {
      toast.error(options.errorMessage)
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context == null) return

      const { sequence } = context
      sequence.pending -= 1
      if (sequence.pending > 0) return

      const queryKey = userKeys.init()
      queryClient.setQueryData<UserInitResponse | undefined>(
        queryKey,
        current => {
          let next = current
          for (const field of sequence.fields.values()) {
            next = field.hasSuccessfulValue
              ? field.updateUser(next, field.latestSuccessfulValue)
              : field.restoreUser(next)
          }
          return next
        }
      )

      measurementPreferenceMutationSequences.delete(queryClient)
      void queryClient.invalidateQueries({ queryKey })
    },
  })
}

/** Serializes the shared measurement-unit preference across mounted surfaces. */
export const useUpsertMeasurementUnitsPreference = (): UseMutationResult<
  unknown,
  Error,
  MeasurementUnitsPreferenceParams,
  MeasurementPreferenceMutationContext
> =>
  useUpsertOptimisticMeasurementPreference({
    mutationKey: measurementUnitsPreferenceMutationKey,
    scopeId: 'map.measurement.units',
    fieldKey: 'map.measurement.units',
    updateUser: withMeasurementUnits,
    errorMessage: 'Unable to save measurement units',
  })

/** Serializes label-setting writes while keeping map labels optimistic. */
export const useUpsertMeasurementLabelsPreference = (): UseMutationResult<
  unknown,
  Error,
  MeasurementLabelsPreferenceParams,
  MeasurementPreferenceMutationContext
> =>
  useUpsertOptimisticMeasurementPreference({
    mutationKey: measurementLabelsPreferenceMutationKey,
    scopeId: 'map.measurement.labels',
    fieldKey: 'map.measurement.labels',
    updateUser: withMeasurementLabelSettings,
    errorMessage: 'Unable to save measurement label settings',
  })
